// 左上角「購買獎勵遊戲 / Buy Bonus」按鈕。
// 對照 Unity SlotFramework BuyTypeButtons.prefab：藍紫霓虹底圖 pic_buy 疊上本地化白描邊字圖 tx_btnBuyBonus，
// 中央疊 30 幀發光序列（BuyBonusLoop.anim：btn_BuyBonus_00000~00058、2.0167s loop；Anim 節點 140×107、
// scale 2 → 有效 280×214 蓋滿按鈕）。disabled 以 alpha 0.5、按下以縮放表現。
// 點擊發 buyBonusClicked → 開啟購買確認彈窗（BuyBonusDialog，對應 Unity BuyBonus.prefab 的 Step2）。
// 免費遊戲期間整顆隱藏（對照 Unity FG 實機版面：左上角讓位給 MULTIPLIER 倍率吊牌）。

import { Container, Sprite, Ticker } from 'pixi.js'
import {
  unityAnchorLandscape,
  unityAnchorPortrait,
  unityScaleLandscape,
  unityScalePortrait,
  type Orientation,
  type Resizable,
} from '../../../framework/render/design'
import type { EventBus } from '../../../framework/events/EventBus'
import type { GameEventMap } from '../../events/GameEvents'
import { BUY_GLOW_DURATION_S, getBuyGlowTextures, getUiTexture } from '../../assets/UiTextures'

/** BuyBonusButton 節點 sizeDelta 194×142（＝pic_buy 原生尺寸，兩個方向都沒被覆寫）。 */
const BG_DESIGN_W = 194
/** 發光幀 Anim 節點 140×107、localScale 2 → 有效 280 寬（蓋滿並溢出按鈕，Unity 原意）。 */
const GLOW_DESIGN_W = 140 * 2
/** BuyTextImage sizeDelta 128×80、`m_PreserveAspect: 1` → 以寬 128 等比內縮。 */
const LABEL_DESIGN_W = 128

/**
 * BuyTypeButtons 根節點 anchoredPosition（皆中心錨定、localScale 1）：
 * 橫版 LandscapeUI.prefab 覆寫為 (−826, 391.4)；直版 PoriraitUI.prefab 覆寫為 (−409.9, −490.5)
 * ——直版**不在左上角**，而是在盤面下方舞台地板的左側。
 */
const ROOT_AX = -826
const ROOT_AY = 391.4
const ROOT_P_AX = -409.9
const ROOT_P_AY = -490.5

export class BuyBonusButton extends Container implements Resizable {
  private readonly bg = new Sprite(getUiTexture('buyBtnBg'))
  private readonly glow: Sprite | null
  private readonly labelSprite = new Sprite(getUiTexture('buyBtnLabel'))
  private glowElapsed = 0

  constructor(private readonly bus: EventBus<GameEventMap>) {
    super()
    this.bg.anchor.set(0.5)
    this.labelSprite.anchor.set(0.5)

    const glowFrames = getBuyGlowTextures()
    this.glow = glowFrames !== null && glowFrames.length > 0 ? new Sprite(glowFrames[0]) : null
    if (this.glow !== null) this.glow.anchor.set(0.5)

    this.addChild(this.bg)
    if (this.glow !== null) this.addChild(this.glow)
    this.addChild(this.labelSprite)

    // 發光序列循環（60fps 取樣 30 幀、2.0167s loop，對照 Unity Animator）。
    if (this.glow !== null) {
      Ticker.shared.add((ticker) => {
        if (!this.visible || this.destroyed || this.glow === null) return
        const frames = getBuyGlowTextures()
        if (frames === null || frames.length === 0) return
        this.glowElapsed = (this.glowElapsed + ticker.deltaMS / 1000) % BUY_GLOW_DURATION_S
        const index = Math.min(frames.length - 1, Math.floor((this.glowElapsed / BUY_GLOW_DURATION_S) * frames.length))
        this.glow.texture = frames[index]!
      })
    }

    this.eventMode = 'static'
    this.cursor = 'pointer'
    this.on('pointertap', () => this.bus.emit('buyBonusClicked', undefined))
    this.on('pointerover', () => this.scale.set(1.04))
    this.on('pointerout', () => this.scale.set(1))
    this.on('pointerdown', () => this.scale.set(0.95))
    this.on('pointerup', () => this.scale.set(1.04))

    // 免費遊戲期間隱藏（FG 中不可購買；左上角位置由倍率吊牌使用）。
    this.bus.on('freeGameStarted', () => {
      this.visible = false
    })
    this.bus.on('freeGameEnded', () => {
      this.visible = true
    })
  }

  /** 將 sprite 依目標寬度等比縮放（維持來源圖原生比例）。 */
  private fitWidth(sprite: Sprite, targetWidth: number): void {
    sprite.scale.set(targetWidth / sprite.texture.width)
  }

  resize(width: number, height: number, orientation: Orientation): void {
    const isPortrait = orientation === 'portrait'
    const s = isPortrait ? unityScalePortrait(width, height) : unityScaleLandscape(width, height)

    this.fitWidth(this.bg, BG_DESIGN_W * s)
    if (this.glow !== null) {
      this.fitWidth(this.glow, GLOW_DESIGN_W * s)
      this.glow.position.set(0, 0)
    }
    this.fitWidth(this.labelSprite, LABEL_DESIGN_W * s)
    this.labelSprite.position.set(0, 0)

    const at = isPortrait
      ? unityAnchorPortrait(ROOT_P_AX, ROOT_P_AY, width, height)
      : unityAnchorLandscape(ROOT_AX, ROOT_AY, width, height)
    this.position.set(at.x, at.y)
  }
}
